import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalStorageService } from 'src/app/shared/services/angular-service/local-storage.service';

@Component({
  selector: 'app-company-timeline',
  templateUrl: './company-timeline.component.html',
  styleUrls: ['./company-timeline.component.scss']
})
export class CompanyTimelineComponent {
  @Output() itemAddedToCart: EventEmitter<any> = new EventEmitter();
  public timelineForm: UntypedFormGroup;
  @Input() all_data: any;
  @Input() cin: any;

  fragment: any;
  companyData: any;

  groupedTimeline = {};
  originalTimeline = {}; // To store original timeline data

  years: string[] = []; 
  plan: any;
  isAdvance: boolean;
  isPremium: boolean;
  isFree: boolean;
  productList: any;
  tag: string[] = [];

  constructor(
    private router: ActivatedRoute,
    private localStorageService: LocalStorageService
  ) {
    this.timelineForm = new UntypedFormGroup({
      tag: new UntypedFormControl(''),
      year: new UntypedFormControl(''),
    });
  }

  ngOnInit(): void {
    this.router.fragment.subscribe((fragment) => {
      this.fragment = fragment;
    });

    this.companyData = this.all_data;
    this.productList = this.all_data.product_list;
    if (!this.all_data.timeline) {
      this.all_data.timeline = [];
    }
    this.plan = this.localStorageService.getItem('plan');
    if (this.plan === 'plan_advance') {
      this.isAdvance = true;
      this.isFree = false;
      this.isPremium = false;
    } else if (this.plan === 'plan_premium') {
      this.isPremium = true;
      this.isAdvance = false;
      this.isFree = false;
    } else {
      this.isFree = true;
      this.isAdvance = false;
      this.isPremium = false;
    }
    this.groupEventsByYear();
    this.extractTags();
    // Store the original grouped timeline data
    this.originalTimeline = { ...this.groupedTimeline };
    // Listen for changes in tag and year dropdowns
    this.timelineForm.get('tag')?.valueChanges.subscribe(() => {
      this.filterTimeline();
    });
    this.timelineForm.get('year')?.valueChanges.subscribe(() => {
      this.filterTimeline();
    });
  }

  extractTags() {
    const tags = this.all_data.timeline.map(event => event.label);
    this.tag = Array.from(new Set(tags)); // Remove duplicate tags
  }

  groupEventsByYear() {
    this.groupedTimeline = this.all_data.timeline.reduce((acc, event) => {
      const year = this.getYear(event.eventDate);
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(event);
      return acc;
    }, {});

    this.years = Object.keys(this.groupedTimeline).sort((a, b) => parseInt(b) - parseInt(a));
    this.years.forEach(year => {
      this.groupedTimeline[year].sort((a, b) => {
        const monthA = this.getMonthNumber(b.eventDate);
        const monthB = this.getMonthNumber(a.eventDate);
        if (monthA === monthB) {
          return parseInt(b.eventDate.split(' ')[0]) - parseInt(a.eventDate.split(' ')[0]); // Sort by day if months are the same
        }
        return monthA - monthB;
      });
    });
  }

  filterTimeline() {
    const selectedTag = this.timelineForm.get('tag')?.value;
    const selectedYear = this.timelineForm.get('year')?.value;
  
    if (!selectedTag && !selectedYear) {
      // Restore original timeline if no filters are selected
      this.groupedTimeline = { ...this.originalTimeline };
      return;
    }
  
    // Filtered timeline based on tag and/or year
    this.groupedTimeline = Object.keys(this.originalTimeline).reduce((acc, year) => {
      // Check if year matches, or no year filter is applied
      if (!selectedYear || year === selectedYear) {
        // Filter events based on selected tag
        const filteredEvents = this.originalTimeline[year].filter(event => {
          return !selectedTag || event.label === selectedTag;
        });
  
        if (filteredEvents.length > 0) {
          acc[year] = filteredEvents;
        }
      }
      return acc;
    }, {});
  }

  
  getYear(eventDate: string) {
    return new Date(eventDate).getFullYear();
  }


  getMonth(eventDate: string): string {
    if (eventDate.length === 4) {
      return this.convertMonthNumberToName(1); // Default to January if only year is provided
    }
  
    if (eventDate.includes('/')) {
      const parts = eventDate.split('/');
      if (parts.length === 2) {
        return this.convertMonthNumberToName(parseInt(parts[0])); // mm/yyyy format
      } else if (parts.length === 3) {
        const monthNumber = parseInt(parts[1]);
        const monthName = this.convertMonthNumberToName(monthNumber);
        const day = parseInt(parts[0]);
        return `${day} ${monthName}`; // dd/mm/yyyy format
      }
    }
  
    const parts = eventDate.split(' ');
    if (parts.length === 2) {
      const monthName = parts[0].toUpperCase().substr(0, 3);
      return `${monthName}`; // Date and month like "16 April"
    }
    if(parts.length === 3){
      const monthName = parts[1].toUpperCase().substr(0, 3);
      return `${parts[0]} ${monthName}`;
    }
  
    const monthName = parts[0].toUpperCase().substr(0, 3);
    return monthName; // Only month like "April"
  }
  
  
  convertMonthNumberToName(monthNumber: number): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthNames[monthNumber - 1];
  }

  getMonthNumber(eventDate: string): number {
    if (eventDate.length === 4) {
      return 1; // Default to January if only year is provided
    }

    if (eventDate.includes('/')) {
      const parts = eventDate.split('/');
      return parseInt(parts[1]); // Return the month part as a number
    }

    const parts = eventDate.split(' ');
    if (parts.length === 3 && !isNaN(Number(parts[0]))) {
      return parseInt(parts[1]); // Return the month part as a number for "16 April 2025"
    }

    const month = parts[0];
    return new Date(Date.parse(month + " 1, 2020")).getMonth() + 1; // Convert month name to a number
  }

  private colorCache: Map<string, string> = new Map();

  generatePastelColor(label: string): string {
    // Check if color already exists for this label
    if (this.colorCache.has(label)) {
      return this.colorCache.get(label)!;
    }

    // Generate a pastel color
    const hue = Math.floor(Math.random() * 360); // Ensures a wide range of colors
    const saturation = 70 + Math.random() * 10; // Saturation for pastel feel
    const lightness = 85 + Math.random() * 10; // Lightness for pastel feel

    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    this.colorCache.set(label, color); // Cache this color for reuse

    return color;
  }
}
